import { useCallback, useRef } from 'react';

export const useDebounce = <Args extends unknown[], Result>(
  func: (...args: Args) => Result,
  delay: number,
): ((...args: Args) => Promise<Awaited<Result>>) => {
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rejectRef = useRef<((reason?: unknown) => void) | null>(null);

  return useCallback(
    (...args: Args) => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }

      if (rejectRef.current) {
        rejectRef.current(new Error('Debounced call cancelled'));
      }

      return new Promise<Awaited<Result>>((resolve, reject) => {
        rejectRef.current = reject;

        timeoutIdRef.current = setTimeout(() => {
          Promise.resolve(func(...args))
            .then(value => resolve(value))
            .catch(reject);
        }, delay);
      });
    },
    [func, delay],
  );
};
